import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { AnimatePresence, motion } from "framer-motion"
import Checkbox from "../../Checkbox"

const CHECKBOX_OPTIONS = [
  { key: "baggage", label: "Только с багажом" },
  { key: "refundable", label: "Возвратные билеты" },
  { key: "direct", label: "Только прямые рейсы" },
  { key: "transfers", label: "С пересадками" },
]

const AdditionalOptionsCard = ({ onChange, nationalities = [] }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [citizenship, setCitizenship] = useState(null)
  const [checkboxes, setCheckboxes] = useState({})
  const ref = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    const isMobile = window.innerWidth < 640
    if (isMobile && open) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [open])

  const toggleCheckbox = (key) => {
    const next = { ...checkboxes, [key]: !checkboxes[key] }
    setCheckboxes(next)
    onChange?.({ citizenship, checkboxes: next })
  }

  const selectCitizenship = (country) => {
    const next = citizenship === country ? null : country
    setCitizenship(next)
    onChange?.({ citizenship: next, checkboxes })
  }

  const handleReset = () => {
    setCitizenship(null)
    setCheckboxes({})
    onChange?.({ citizenship: null, checkboxes: {} })
  }

  const activeCount = (citizenship ? 1 : 0) + Object.values(checkboxes).filter(Boolean).length

  return (
    <>
      <div className="relative w-full sm:w-auto" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full sm:w-auto flex items-center gap-2 bg-white rounded-md px-4 h-[52px] border border-transparent hover:border-gray-200 transition-colors text-sm text-gray-500 font-medium"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0"
          >
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="18" x2="20" y2="18" />
            <circle cx="9" cy="6" r="2" fill="white" />
            <circle cx="15" cy="12" r="2" fill="white" />
            <circle cx="9" cy="18" r="2" fill="white" />
          </svg>

          <span className="flex-1 text-left whitespace-nowrap">
            {t("filter.additionalOptions", "Дополнительные опции")}
          </span>

          <span className="w-[18px] h-[18px] flex items-center justify-center shrink-0">
            <AnimatePresence mode="popLayout">
              {activeCount > 0 && (
                <motion.span
                  key="badge"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  className="min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[11px] font-semibold flex items-center justify-center"
                >
                  {activeCount}
                </motion.span>
              )}
            </AnimatePresence>
          </span>

          <motion.svg
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="shrink-0 text-gray-400"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </motion.svg>
        </button>

        {/* Desktop dropdown */}
        <AnimatePresence>
          {open && (
            <motion.div
              key="desktop-dropdown"
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="hidden sm:block absolute top-full left-0 mt-1 z-50 bg-white rounded-md shadow-lg border border-gray-100 w-80 origin-top-left"
            >
              <DropdownContent
                citizenship={citizenship}
                checkboxes={checkboxes}
                activeCount={activeCount}
                onSelectCitizenship={selectCitizenship}
                onToggleCheckbox={toggleCheckbox}
                onReset={handleReset}
                t={t}
                items={nationalities}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile bottom sheet */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-sheet-wrapper"
            className="sm:hidden fixed inset-0 z-50 flex flex-col justify-end"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />

            <motion.div
              className="relative bg-white rounded-t-2xl shadow-xl max-h-[85vh] flex flex-col"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 350, damping: 35 }}
            >
              <div className="flex justify-center pt-3 pb-1 shrink-0">
                <div className="w-10 h-1 rounded-full bg-gray-200" />
              </div>

              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
                <span className="text-base font-medium text-gray-800">
                  {t("filter.additionalOptions", "Дополнительные опции")}
                </span>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className="overflow-y-auto flex-1">
                <DropdownContent
                  citizenship={citizenship}
                  checkboxes={checkboxes}
                  activeCount={activeCount}
                  onSelectCitizenship={selectCitizenship}
                  onToggleCheckbox={toggleCheckbox}
                  onReset={handleReset}
                  t={t}
                  items={nationalities}
                />
              </div>

              <div className="shrink-0 h-[env(safe-area-inset-bottom,16px)]" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

const DropdownContent = ({
  citizenship,
  checkboxes,
  activeCount,
  onSelectCitizenship,
  onToggleCheckbox,
  onReset,
  t,
  items = [],
}) => {
  const [search, setSearch] = useState("")

  const filtered = items.filter((item) => item.toLowerCase().includes(search.toLowerCase()))

  return (
    <>
      <div className="p-4 border-b border-gray-100">
        <p className="text-xs text-gray-400 font-medium mb-3 uppercase tracking-wide">
          {t("filter.citizenship", "Гражданство")}
        </p>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("filter.searchCountry", "Поиск страны...")}
          className="w-full mb-2 px-3 py-1.5 text-sm border border-gray-200 rounded-md outline-none focus:border-orange-400 transition-colors"
        />

        <div className="grid grid-cols-2 gap-1 h-48 overflow-y-auto content-start">
          {filtered.length > 0 ? (
            filtered.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => onSelectCitizenship(item)}
                className={`text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  citizenship === item
                    ? "bg-orange-50 text-orange-500 font-medium"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {item}
              </button>
            ))
          ) : (
            <p className="col-span-2 text-sm text-gray-400 py-2 px-1">
              {t("filter.noResults", "Ничего не найдено")}
            </p>
          )}
        </div>
      </div>

      {/* <div className="p-4 border-b border-gray-100">
        <p className="text-xs text-gray-400 font-medium mb-3 uppercase tracking-wide">
          {t("filter.preferences", "Параметры")}
        </p>
        <div className="flex flex-col gap-3">
          {CHECKBOX_OPTIONS.map((opt) => (
            <Checkbox
              key={opt.key}
              checked={!!checkboxes[opt.key]}
              onChange={() => onToggleCheckbox(opt.key)}
              label={opt.label}
            />
          ))}
        </div>
      </div> */}

      <AnimatePresence>
        {activeCount > 0 && (
          <motion.div
            key="reset-row"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-3">
              <button
                type="button"
                onClick={onReset}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                {t("filter.resetAll", "Сбросить всё")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default AdditionalOptionsCard
